import { useCallback, useMemo } from 'react';
import { Control, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import api from 'lib/trpcClient';
import { AssetType } from 'further-types/lp/asset';
import { investment as rules } from 'further-ui/validations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { SortOrder } from 'further-types/utils';

export interface AssetOption {
  id: string;
  title: string;
  baseCurrency: CurrencyCode;
}

export const useAsset = ({
  control,
  watch,
  setValue,
  assetCurrency,
  vehicleId,
}: {
  control: Control<rules.CreateInvestmentFormValues>;
  watch: UseFormWatch<rules.CreateInvestmentFormValues>;
  setValue: UseFormSetValue<rules.CreateInvestmentFormValues>;
  assetCurrency: CurrencyCode;
  vehicleId: string;
}) => {
  const selectedAssetId = watch('assetId');
  const isNewAsset = watch('isNewAsset') && !selectedAssetId;

  const { data: assets, isLoading } = api.asset.list.useQuery({
    assetType: AssetType.Company,
    order: SortOrder.Desc,
    orderBy: 'createdAt',
    vehicleId,
  });

  const assetOptions = useMemo<Array<AssetOption>>(
    () =>
      assets?.result.map((asset) => ({
        id: asset._id,
        title: asset.assetName,
        baseCurrency: asset.baseCurrency,
      })) ?? [],
    [assets],
  );

  const resetAssetDropdown = useCallback(() => {
    setValue('assetId', '');
    setValue('holdingClasses', [
      {
        isNewHoldingClass: true,
        currency: assetCurrency,
        name: '',
        holdingClassId: '',
        investmentAmountTcy: 0,
        numberOfUnits: 0,
        unitPriceTcy: 0,
      },
    ]);
  }, [setValue]);

  return {
    control,
    selectedAssetId,
    isNewAsset,
    assetOptions,
    resetAssetDropdown,
    isLoading,
  };
};
